import { AuthenticationContext } from "@/app/context/authentication/authenticationContext";
import { Text, Button, Modal, ModalBody, Flex, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useDisclosure, Image, Alert, AlertIcon, CircularProgress, Badge, ButtonGroup, VStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { FirebaseContext } from "@/app/context/firebase/firebaseContext";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { NotificationContext } from "@/app/context/notification/notificationContext";
import { FaCamera } from "react-icons/fa6";
import Webcam from "react-webcam";
import { getAnalysis } from "@/app/helpers/chatGPTHelper";
import { QuizzType } from "@/app/types/quizzType";
import { set } from "lodash";
import { WebviewType } from "@/app/types/webviewType";
import { GameQuestContext } from "@/app/context/game/gameQuestContext";
import { extractJsonFromText } from "@/app/helpers/extractJsonFromString";

type Props = {
    isOpen: boolean;
    handleClose: () => void;
};

export default function CapturePhotoEnvironment() {
    const { t } = useTranslation();
    const {onOpen, isOpen, onClose} = useDisclosure();
    const { cloudStorage, user, isAuthLoading, setShowLoginModal } = useContext(AuthenticationContext);
    const { setDoVibrate, addError, addMessageWithId, character } = useContext(NotificationContext);
    const { logAnalyticsEvent } = useContext(FirebaseContext);
    const { addOrUpdateActionHistory, addPoint, setShowConfetti, play, increaseNaturePhotoCount, dailyNaturePhotoCount, } = useContext(GlobalDataUserContext);
    const { language,imageBase64 } = useContext(GlobalDataContext);

    const webcamRef = useRef<Webcam | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [quizz, setQuizz] = useState<QuizzType | null>(null);
    const [quizzResult, setQuizzResult] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const {
        quest,
        increaseQuizzSuccessCount
    } = useContext(GameQuestContext)
    
    const handleClose=()=>{
        onClose();
    }

    const handleCloseQuizz=()=>{
        setQuizz(null);
        setQuizzResult(null);
    }

    const answerQuizz=(index:number)=>{
        if (quizz) {
            if (index === quizz.goodAnswerIndex) {
                increaseNaturePhotoCount()
                addOrUpdateActionHistory("quizz",1)
                play("woohoo01")
                setQuizzResult(true)
                setShowConfetti(true)
                addPoint(250,0)
            }else{
                setQuizzResult(false)
            }
        }
    }

    useEffect(() => {
        if ( imageBase64){
            setIsLoading(true);
            setImageSrc(imageBase64); // This will store the captured image as base64
            getAnalysis(imageBase64, language, user)
            .then((result)=>{ 
                if ( result ) {
                    const data = extractJsonFromText(result)
                    if ( data && !data.error) {
                        setQuizz(data)
                        setIsLoading(false);
                        onClose()
                    }else{
                        addError(t("quizz.Error with photo"),"error")
                        setImageSrc(null);
                        setIsLoading(false);
                    }
                }else{
                    addError(t("quizz.Error with photo"),"error")
                    setImageSrc(null);
                    setIsLoading(false);
                }
            })
            .catch((error)=>{   
                setIsLoading(false);
            })
        }
    }, [imageBase64]);

    const capturePhoto = () => {
        onClose();
        setQuizzResult(null);
        if (webcamRef.current && !imageSrc) {
            const imageSrc = webcamRef.current.getScreenshot();
            if (imageSrc) {
                logAnalyticsEvent("quizz",{})                
                setIsLoading(true);
                setImageSrc(imageSrc); // This will store the captured image as base64
                getAnalysis(imageSrc, language, user)
                .then((result)=>{ 
                    if ( result ) {
                        const data = extractJsonFromText(result)
                        if ( data && !data.error) {
                            increaseNaturePhotoCount()
                            setQuizz(data)
                            setIsLoading(false);
                            onClose()
                        }else{
                            addError(t("quizz.Error with photo"),"error")
                            setImageSrc(null);
                            setIsLoading(false);
                        }
                    }else{
                        addError(t("quizz.Error with photo"),"error")
                        setImageSrc(null);
                        setIsLoading(false);
                    }
                })
                .catch((error)=>{   
                    setIsLoading(false);
                })
            }
        }else{
            setImageSrc(null);
        }
    };

    const openCamera = () => {
        setImageSrc(null);
        if (window.ReactNativeWebView) {
            const data:WebviewType= {
                domain: "GOOBLIES",
                action: "openCamera",
                payload: {
                    title: t("quizz.titlePhoto"),
                    description: t("quizz.descriptionCapture"),
                    buttonClose: t("quizz.fermer"),
                    buttonCapture: t("quizz.capture")
                }
            }
            window.ReactNativeWebView.postMessage(JSON.stringify(data));
        }else {
            onOpen();
        }
    }

    return (
        <>
            
            {dailyNaturePhotoCount <= 10 && <ButtonGroup className="mainButton" position="relative" onClick={openCamera} background="#6c27b7!important" marginX="auto" variant='solid' size="lg" borderRadius="50px" isAttached >
                <Button background="#6c27b7!important" color="white" borderRadius="50%!important" margin="auto"  p="0" w="50px"fontSize={"1.5em"}>
                    <FaCamera />                    
                </Button>
                <Button lineHeight={1} pl={0} borderRadius="50px"background="#6c27b7!important" className="pulse-yellow" color="white!important">
                    {t("quizz.nature")}
                </Button>
                <Badge zIndex={2} position="absolute" bottom={0} right={-2} colorScheme='gray' textTransform={"none"}>
                    {dailyNaturePhotoCount}/10
                </Badge>
            </ButtonGroup>}
            
            {dailyNaturePhotoCount > 10 && <ButtonGroup className="mainButton" position="relative" background="#656565!important" marginX="auto" variant='solid' size="lg" borderRadius="50px" isAttached >
                <Button background="#656565!important" color="white" borderRadius="50%!important" margin="auto"  p="0" w="50px"fontSize={"1.5em"}>
                    <FaCamera />                    
                </Button>
                <Button lineHeight={1} pl={0} borderRadius="50px"background="#656565!important" className="pulse-yellow" color="white!important">
                    {t("quizz.nature")}
                </Button>
                <Badge zIndex={2} position="absolute" bottom={0} right={-2} colorScheme='gray' textTransform={"none"}>
                    {dailyNaturePhotoCount}/10
                </Badge>
            </ButtonGroup>}

            <Modal isCentered isOpen={isOpen} onClose={handleClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader pt={2} pb={0} textAlign={"center"}>{t("quizz.titlePhoto")}</ModalHeader>
                    <ModalCloseButton />
                    {!isAuthLoading && user && <ModalBody p={0} px={2}>
                        <Text textAlign="center">{t("quizz.descriptionCapture")}</Text>
                        {imageSrc ? (
                            // Display the captured image
                            <Image src={imageSrc} alt="Captured" style={{ width: "100%" }} />
                        ) : (
                            // Display the webcam video stream
                            <Webcam
                                ref={webcamRef}
                                screenshotFormat="image/png"
                                width="100%"
                                videoConstraints={{
                                    facingMode: "environment", // Rear camera for mobile
                                }}
                            />
                        )}
                    </ModalBody>}
                    {!isAuthLoading && !user && <ModalBody px={2}> 
                        <VStack alignItems={"start"} gap={0}>
                        <Alert status="warning">
                            <AlertIcon />
                            <VStack>
                                <Text>{t("game.mustBeConnected")}</Text>
                                <Button onClick={()=>{setShowLoginModal(true)}} colorScheme="orange" size="sm" mt={2}>{t("quest.login")}</Button> 
                            </VStack>
                            </Alert>
                        </VStack>
                    </ModalBody>}
                    <ModalFooter px={2} pt={2} pb={2} display="flex" justifyContent={"flex-end"}>
                        <Button colorScheme="gray" size="sm" mr={2} onClick={handleClose}>
                            {t("quizz.fermer")}
                        </Button>
                        {!isAuthLoading && user && <Button background="#6c27b7!important" color="white" size="sm" onClick={capturePhoto}>
                            {t("quizz.capture")}
                        </Button>}                   
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal closeOnOverlayClick={false} isCentered isOpen={!!quizz || isLoading} onClose={handleCloseQuizz}>
                <ModalOverlay />
                <ModalContent maxH="80vh" overflow={"hidden"}>
                    <ModalHeader pt={2} pb={0} textAlign={"center"}>{quizz?.answers[quizz.goodAnswerIndex].toUpperCase()}</ModalHeader>
                    <ModalBody p={0} px={2} overflow="auto">
                        {isLoading && <>
                            <Flex w="full" h="100px">
                                <CircularProgress margin="auto" isIndeterminate color='blue.300' />
                            </Flex>
                        </>}
                        {!isLoading && <>
                            {imageSrc &&
                                // Display the captured image
                                <Image src={imageSrc} p={5} margin="auto" alt="Captured" style={{ width: "75%" }} />
                            }                            
                            <>
                                <Text pt={3} textAlign="center">{quizz?.information}</Text>
                            </>
                            
                        </>}
                    </ModalBody>
                    <ModalFooter px={2} pt={2} pb={2} display="flex" gap={3} justifyContent={"flex-end"}>                        
                        <Button colorScheme="gray" size="sm" onClick={handleCloseQuizz}>
                            {t("quizz.fermer")}
                        </Button>                        
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
